import { calculatePosition, type CalculationTemplate } from "@shared/calculation-template";
import type { LVSektion } from "@/components/project/leistungsverzeichnis-kalkulator";
import { applyCalculationTemplate, templateOfferPositionId, type TemplateOfferPosition } from "./apply-calculation-template";
export { parseMeasurementFile } from "./measurement-file";

import type { MeasurementRow, MeasurementWorkbook } from "@shared/measurement";
export type { MeasurementRow, MeasurementTable, MeasurementWorkbook } from "@shared/measurement";
export interface MeasurementTarget {
  id: string; code: string; name: string; unit: string; section: string; ep: number;
  existing: boolean; existingQty?: number; template?: TemplateOfferPosition;
}
export interface MeasurementSelection { row: MeasurementRow; target: MeasurementTarget; fileName: string; sheet: string }

const normalizedName = (name: string) => name.trim().toLocaleLowerCase("de")
  .replace(/\s+/g, " ");
const normalizedCode = (code: string) => code.trim().toUpperCase();
export function normalizedMeasurementUnit(unit: string) {
  const value = unit.trim().toLowerCase().replace(/\s+|\./g, "");
  if (["m²", "m2", "qm", "quadratmeter"].includes(value)) return "m²";
  if (["m³", "m3", "cbm", "kubikmeter"].includes(value)) return "m³";
  if (["m", "lfm", "lfdm", "lm", "laufmeter"].includes(value)) return "m";
  if (["stk", "st", "stück", "stueck", "pcs"].includes(value)) return "stk";
  if (["pa", "psch", "pau", "pauschal", "pauschale"].includes(value)) return "pa";
  if (["h", "std", "stunden", "stunde"].includes(value)) return "h";
  return value;
}
export function compatibleMeasurementUnits(a: string, b: string) {
  const first = normalizedMeasurementUnit(a), second = normalizedMeasurementUnit(b);
  return !!first && !!second && first !== "ohneeinheit" && second !== "ohneeinheit" && first === second;
}

export function buildMeasurementTargets(sections: LVSektion[], template?: CalculationTemplate): MeasurementTarget[] {
  const targets: MeasurementTarget[] = sections.flatMap((section) => section.positionen.map((position) => {
    const leadingCode = position.name.match(/^([0-9][0-9A-Za-z._-]*)\s+/)?.[1] ?? "";
    const ownTemplatePosition = template?.positions.find((source) =>
      templateOfferPositionId({ ...source, templateId: template.id, ep: 0 }) === position.id);
    return {
      id: position.id, code: ownTemplatePosition?.code ?? leadingCode,
      name: position.name, unit: position.unit, section: section.id, ep: position.ep,
      existing: true, existingQty: position.qty,
    };
  }));
  for (const position of template?.positions ?? []) {
    const source: TemplateOfferPosition = {
      templateId: template!.id, code: position.code, name: position.name, unit: position.unit,
      section: position.section, ep: calculatePosition(position).price,
      calculation: structuredClone(position),
      ...(template!.sectionTitles?.[position.section] ? { sectionTitle: template!.sectionTitles[position.section] } : {}),
    };
    const id = templateOfferPositionId(source);
    if (targets.some((target) => target.id === id)) continue;
    targets.push({ ...source, id, existing: false, template: source });
  }
  return targets;
}

export function matchMeasurementRow(row: MeasurementRow, targets: MeasurementTarget[]): { targetId: string | null; reason: string } {
  if (row.issues.length || row.quantity === null || !Number.isFinite(row.quantity) || row.quantity < 0 || row.quantity > 1e9) {
    return { targetId: null, reason: row.issues.join(" ") || "Ungültige Menge." };
  }
  const matching = targets.filter((target) => row.code
    ? normalizedCode(target.code) === normalizedCode(row.code)
    : normalizedName(target.name.replace(/^[0-9][0-9A-Za-z._-]*\s+/, "")) === normalizedName(row.name));
  const compatible = matching.filter((target) => compatibleMeasurementUnits(row.unit, target.unit));
  // Updating an existing offer quantity is preferable to adding another copy
  // from a newly selected price template.
  const existing = compatible.filter((target) => target.existing);
  const candidates = existing.length ? existing : compatible;
  if (candidates.length === 1) return { targetId: candidates[0].id, reason: "Positionsnummer/Bezeichnung und Einheit stimmen überein." };
  if (candidates.length > 1) return { targetId: null, reason: "Mehrere passende Positionen. Bitte eindeutig zuordnen." };
  return { targetId: null, reason: matching.length ? "Einheit stimmt nicht mit der LV-Position überein." : "Keine eindeutige Position gefunden. Bitte zuordnen oder überspringen." };
}

function sourceDescription(selection: MeasurementSelection) {
  const { row, fileName, sheet } = selection;
  const source = `Maßimport: ${fileName.slice(0, 255)} · ${sheet.slice(0, 100)}${row.sourcePage ? ` · PDF-Seite ${row.sourcePage}` : ""} · Zeile ${row.rowNumber}`;
  return `${source}\n${row.code} ${row.name}: ${row.quantity} ${row.unit}${row.note ? `\nHinweis: ${row.note}` : ""}`;
}

export function applyMeasurementSelections(sections: LVSektion[], selections: MeasurementSelection[]) {
  if (!selections.length) throw new Error("Bitte mindestens eine gültige Menge auswählen.");
  const selectedIds = new Set<string>(), sourceRows = new Set<string>();
  const current = new Map(sections.flatMap((section) => section.positionen.map((position) => [position.id, position] as const)));
  for (const selection of selections) {
    const { row, target } = selection;
    if (row.quantity === null || !Number.isFinite(row.quantity) || row.quantity < 0 || row.quantity > 1e9 || row.issues.length) {
      throw new Error(`Zeile ${row.rowNumber}: ungültige Menge.`);
    }
    if (!compatibleMeasurementUnits(row.unit, target.unit)) throw new Error(`Zeile ${row.rowNumber}: Einheit passt nicht.`);
    if (selectedIds.has(target.id)) throw new Error("Mehrere Zeilen sind derselben LV-Position zugeordnet. Bitte vor dem Import eindeutig zusammenstellen.");
    if (sourceRows.has(row.id)) throw new Error("Eine Quellzeile darf nur einmal übernommen werden.");
    if (!sections.some((section) => section.id === target.section)) throw new Error("Leistungsgruppe ist im Angebot nicht vorhanden.");
    const existing = current.get(target.id);
    if (target.existing && !existing) throw new Error("Die Zielposition wurde inzwischen entfernt. Bitte neu zuordnen.");
    if (existing && !compatibleMeasurementUnits(row.unit, existing.unit)) throw new Error("Die Einheit der Zielposition wurde inzwischen geändert.");
    if (!existing && (!target.template || templateOfferPositionId(target.template) !== target.id ||
      !compatibleMeasurementUnits(row.unit, target.template.unit))) throw new Error("Keine gültige Kalkulationsposition für diese Menge.");
    selectedIds.add(target.id); sourceRows.add(row.id);
  }
  const additions = selections.filter(({ target, row }) => !current.has(target.id) && row.quantity !== 0)
    .map(({ target }) => target.template!);
  const copied = applyCalculationTemplate(sections, additions);
  const byId = new Map(selections.map((selection) => [selection.target.id, selection]));
  const changedIds: string[] = [];
  const next = copied.map((section) => ({
    ...section,
    positionen: section.positionen.map((position) => {
      const selection = byId.get(position.id);
      if (!selection) return position;
      changedIds.push(position.id);
      // Keep unrelated project notes, replace only the previous import block.
      const notes = (position.beschreibung ?? "").split("\n\nMaßimport:")[0];
      const retained = notes.startsWith("Maßimport:") ? "" : notes;
      return {
        ...position, qty: selection.row.quantity!, aktiv: selection.row.quantity! > 0,
        beschreibung: `${retained ? `${retained}\n\n` : ""}${sourceDescription(selection)}`,
      };
    }),
  }));
  return { sections: next, changedIds };
}
